import React, { useState, useContext } from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VerifiedIcon from '@mui/icons-material/Verified';
import { AuthContext } from '../../context/AuthContext';
import axios from '../../api/client';
import { toast } from 'react-toastify';

// Which stage endpoint applies at the item's current status, and which permission it
// needs. Kept in one place so the buttons and the request can't disagree.
const STAGE_FOR_STATUS = {
  pending_review: { path: 'review', permission: 'review', label: 'Review' },
  pending_approval: { path: 'final-approval', permission: 'approve', label: 'Final Approval' },
};

/**
 * The three-stage approval actions for any resource that uses the shared workflow.
 *
 * @param item             the record; needs `_id` and `approvalStatus`
 * @param resourcePath     API segment, e.g. 'repairs' or 'requisitions'
 * @param permissionModule permission module name, usually the same word
 * @param itemLabel        shown in the confirmation dialog so the user knows what they're deciding on
 * @param noun             used in the warning copy ('repair', 'requisition')
 * @param onChanged        called after a successful transition so the parent can refetch
 */
const ApprovalActions = ({
  item,
  resourcePath,
  permissionModule,
  itemLabel,
  noun = 'item',
  onChanged,
  size = 'small',
}) => {
  const { can } = useContext(AuthContext);
  const [dialog, setDialog] = useState(null); // { action, stage }
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const status = item.approvalStatus || 'draft';
  const stage = STAGE_FOR_STATUS[status];

  const canSubmit = status === 'draft' && can(permissionModule, 'update');
  const canDecide = !!stage && can(permissionModule, stage.permission);

  const closeDialog = () => {
    setDialog(null);
    setComment('');
  };

  const handleSubmitForReview = async () => {
    setBusy(true);
    try {
      await axios.post(`/${resourcePath}/${item._id}/submit`);
      toast.success('Sent for review');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not submit for review');
    } finally {
      setBusy(false);
    }
  };

  const handleDecision = async () => {
    // The server rejects a comment-less query or rejection; check here too so the user
    // finds out before a round trip.
    if (dialog.action !== 'approve' && !comment.trim()) {
      toast.error(`Please explain why — this comment is sent back with the ${noun}`);
      return;
    }

    setBusy(true);
    try {
      await axios.post(`/${resourcePath}/${item._id}/${dialog.stage.path}`, {
        action: dialog.action,
        comment: comment.trim() || undefined,
      });
      const done = {
        approve: 'Approved',
        query: 'Queried — sent back to draft',
        reject: 'Rejected',
      };
      toast.success(done[dialog.action]);
      closeDialog();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not record decision');
    } finally {
      setBusy(false);
    }
  };

  if (!canSubmit && !canDecide) return null;

  const dialogTitles = {
    approve: `${dialog?.stage?.label}: approve`,
    query: `${dialog?.stage?.label}: query`,
    reject: `${dialog?.stage?.label}: reject`,
  };

  return (
    <>
      <Box display="flex" gap={1} flexWrap="wrap">
        {canSubmit && (
          <Button
            size={size}
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmitForReview}
            disabled={busy}
          >
            Submit for Review
          </Button>
        )}

        {canDecide && (
          <>
            <Button
              size={size}
              variant="contained"
              color="success"
              startIcon={status === 'pending_review' ? <RateReviewIcon /> : <VerifiedIcon />}
              onClick={() => setDialog({ action: 'approve', stage })}
              disabled={busy}
            >
              {status === 'pending_review' ? 'Approve Review' : 'Final Approve'}
            </Button>
            <Button
              size={size}
              variant="outlined"
              color="warning"
              onClick={() => setDialog({ action: 'query', stage })}
              disabled={busy}
            >
              Query
            </Button>
            <Button
              size={size}
              variant="outlined"
              color="error"
              onClick={() => setDialog({ action: 'reject', stage })}
              disabled={busy}
            >
              Reject
            </Button>
          </>
        )}
      </Box>

      <Dialog open={!!dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog ? dialogTitles[dialog.action] : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {itemLabel}
          </Typography>

          {dialog?.action === 'query' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This sends the {noun} back to draft so it can be edited in response to your
              comment. The approval process restarts from review once resubmitted.
            </Alert>
          )}
          {dialog?.action === 'reject' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Rejecting closes this {noun}. It cannot be resubmitted.
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            margin="normal"
            label={dialog?.action === 'approve' ? 'Comment (optional)' : 'Comment'}
            required={dialog?.action !== 'approve'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            helperText={
              dialog?.action === 'approve'
                ? 'Recorded in the approval history'
                : 'Required — explains what needs to change or why this was rejected'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleDecision}
            variant="contained"
            color={dialog?.action === 'reject' ? 'error' : 'primary'}
            disabled={busy || (dialog?.action !== 'approve' && !comment.trim())}
          >
            {busy ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApprovalActions;
